import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { normalizeContactPhone } from './onboarding-draft';
import '../features/field-mobile/auth-remember.css';
import { RegistrationSiteOptionsRequestGate } from './registration-site-options';
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  UserRound,
} from 'lucide-react';

import { BrandMark } from '@kop/ui';
import { FeatureLoading } from '../features/feature-skeleton';

import type {
  AuthAdapter,
  PendingSession,
  RegistrationAccountType,
  RegistrationOptions,
  RegistrationProfileId,
  RegistrationRequiredSession,
} from './types';

export function ContractorCandidateSelect({
  contractors,
}: {
  contractors: RegistrationOptions['contractors'];
}) {
  return (
    <select name="contractorCandidateId" defaultValue="" disabled={contractors.length === 0}>
      <option value="" disabled>
        {contractors.length === 0 ? '등록된 협력업체가 없습니다' : '협력업체를 선택하세요'}
      </option>
      {[...contractors]
        .sort((a, b) => a.label.localeCompare(b.label, 'ko-KR'))
        .map((item) => (
          <option key={item.candidateId} value={item.candidateId}>
            {item.label.trim()}
          </option>
        ))}
    </select>
  );
}

type Mode = 'signin' | 'signup';

const GENERIC_ERROR =
  '입력 정보를 다시 확인해 주세요. 문제가 계속되면 관리 담당자에게 문의해 주세요.';

export function describeAuthenticationError(error: unknown): string {
  const providerCode =
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof error.code === 'string' &&
    /^(auth|functions)\/[a-z0-9-]+$/i.test(error.code)
      ? error.code.toLowerCase()
      : null;
  const value = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  if (
    ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(
      providerCode ?? '',
    ) ||
    /auth\/(invalid-credential|wrong-password|user-not-found|invalid-login-credentials)/i.test(
      value,
    )
  )
    return '이메일 또는 비밀번호가 일치하지 않습니다. 비밀번호를 다시 확인해 주세요.';
  if (providerCode === 'auth/too-many-requests' || /auth\/too-many-requests/i.test(value))
    return '로그인 시도가 일시적으로 제한되었습니다. 잠시 후 다시 시도해 주세요.';
  if (
    providerCode === 'auth/network-request-failed' ||
    /auth\/network-request-failed|network|failed to fetch/i.test(value)
  )
    return '네트워크 연결을 확인한 후 다시 시도해 주세요.';
  if (providerCode === 'auth/user-disabled')
    return '사용이 중지된 계정입니다. 관리 담당자에게 계정 활성화를 요청해 주세요.';
  if (providerCode === 'auth/invalid-email') return '이메일 주소 형식을 확인해 주세요.';
  if (providerCode === 'auth/operation-not-allowed')
    return '이 계정의 로그인 방식이 활성화되지 않았습니다. 관리 담당자에게 문의해 주세요.';
  if (providerCode === 'auth/app-not-authorized' || providerCode === 'auth/invalid-api-key')
    return '로그인 서비스 설정을 확인하지 못했습니다. 관리 담당자에게 문의해 주세요.';
  if (/verify the signup email|email.*verif/i.test(value))
    return '이메일 인증이 완료되지 않았습니다. 인증 메일을 확인해 주세요.';
  if (/functions\/unauthenticated|recent sign-in/i.test(value))
    return '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.';
  if (/functions\/failed-precondition|app check/i.test(value))
    return '보안 확인을 완료하지 못했습니다. 화면을 새로고침한 후 다시 시도해 주세요.';
  if (
    ['functions/internal', 'functions/unavailable', 'functions/resource-exhausted'].includes(
      providerCode ?? '',
    )
  )
    return '서버 사용량이 일시적으로 많습니다. 잠시 후 같은 버튼을 다시 눌러 주세요.';
  return GENERIC_ERROR;
}

export function authenticationInputIsValid(
  mode: Mode,
  email: string,
  password: string,
  displayName: string,
): boolean {
  return (
    email.includes('@') &&
    password.length > 0 &&
    (mode !== 'signup' || (password.length >= 8 && displayName.length >= 2))
  );
}

function AccessIdentity() {
  return (
    <div className="access-identity">
      <BrandMark variant="horizontal" />
    </div>
  );
}

function AccessBackground() {
  return (
    <video
      className="av"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster="/auth/p.webp"
      aria-hidden="true"
      onLoadedMetadata={(event) => {
        event.currentTarget.playbackRate = 0.8;
      }}
    >
      <source src="/auth/v.webm" />
      <source src="/auth/v.mp4" />
    </video>
  );
}

export function AuthScreen({ adapter }: { adapter: AuthAdapter }) {
  const [mode, setMode] = useState<Mode>('signin');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!busy || mode !== 'signin') return;
    const timer = setTimeout(() => {
      setBusy(false);
      setError('계정 확인이 지연되고 있습니다. 화면을 새로고침한 후 다시 로그인해 주세요.');
    }, 30_000);
    return () => clearTimeout(timer);
  }, [busy, mode]);
  const [verificationSent, setVerificationSent] = useState(false);
  const [invite, setInvite] = useState<{ invitationId: string; proof: string } | null>(null);
  const returningFromVerification =
    new URLSearchParams(window.location.search).get('registration') === 'complete';

  useEffect(() => {
    void adapter.consumeRegistrationInvite().then(setInvite);
  }, [adapter]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    const data = new FormData(event.currentTarget);
    const email = String(data.get('email') ?? '').trim();
    const password = String(data.get('password') ?? '');
    const displayName = String(data.get('displayName') ?? '').trim();
    if (!authenticationInputIsValid(mode, email, password, displayName)) {
      setError(GENERIC_ERROR);
      return;
    }
    setBusy(true);
    let awaitingSessionTransition = false;
    try {
      if (mode === 'signup') {
        await adapter.signUp({ displayName, email, password, invite });
        sessionStorage.setItem('kop-registration-name', displayName);
        setVerificationSent(true);
      } else {
        await adapter.signIn({ email, password, rememberMe: data.get('rememberMe') === 'on' });
        /* A successful credential exchange is not yet an admitted workspace session.
         * Keep this surface in place until the observer settles identity, onboarding and
         * scope state; clearing it here briefly paints the sign-in form again. */
        awaitingSessionTransition = true;
      }
    } catch (cause) {
      setError(describeAuthenticationError(cause));
    } finally {
      if (!(mode === 'signin' && awaitingSessionTransition)) setBusy(false);
    }
  };

  return (
    <>
    {busy && mode === 'signin' && (
      <main className="access-loading">
        <FeatureLoading label="사용자 계정을 확인하고 있습니다." />
      </main>
    )}
    <main className="access-shell" style={busy && mode === 'signin' ? { display: 'none' } : undefined}>
      <AccessBackground />
      <section className="access-panel">
        <div className="access-form-wrap">
          <AccessIdentity />
          <header>
            <h2>안전보건시스템</h2>
            <p>
              {mode === 'signin'
                ? '회사 또는 승인된 협력사 계정으로 계속하세요.'
                : '가입 후 관리자가 소속과 역할을 확인합니다.'}
            </p>
            {returningFromVerification && mode === 'signin' && (
              <p className="access-notice" role="status">
                이메일 인증이 완료되었습니다. 로그인해 가입을 마무리하세요.
              </p>
            )}
            {verificationSent && mode === 'signup' && (
              <p className="access-notice" role="status">
                인증 메일을 보냈습니다. 링크 확인 후 이 화면에서 로그인하세요.
              </p>
            )}
          </header>
          <div className="access-tabs" role="tablist" aria-label="계정 접근 방식">
            <button
              role="tab"
              aria-selected={mode === 'signin'}
              onClick={() => {
                setMode('signin');
                setError('');
              }}
            >
              로그인
            </button>
            <button
              role="tab"
              aria-selected={mode === 'signup'}
              onClick={() => {
                setMode('signup');
                setError('');
              }}
            >
              가입 요청
            </button>
          </div>
          <form className="system-form" onSubmit={submit} noValidate>
            {mode === 'signup' && (
              <label>
                <span>이름</span>
                <div className="field-control">
                  <UserRound size={18} />
                  <input name="displayName" autoComplete="name" placeholder="실명을 입력하세요" />
                </div>
              </label>
            )}
            <label>
              <span>이메일</span>
              <div className="field-control">
                <Mail size={18} />
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.co.kr"
                />
              </div>
            </label>
            <label>
              <span>비밀번호</span>
              <div className="field-control">
                <LockKeyhole size={18} />
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  placeholder="8자 이상 입력하세요"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>
            {mode === 'signin' && <label className="auth-remember"><input type="checkbox" name="rememberMe" defaultChecked /><span>로그인 유지</span></label>}
            {error && (
              <p className="form-message error" role="alert">
                {error}
              </p>
            )}
            <button className="system-primary" disabled={busy} type="submit">
              {busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />}
              <span>
                {busy ? '확인 중' : mode === 'signin' ? '업무 화면으로' : '승인 요청 보내기'}
              </span>
            </button>
          </form>
        </div>
      </section>
    </main>
    </>
  );
}

export function RegistrationCompletionScreen({
  session,
  adapter,
}: {
  session: RegistrationRequiredSession;
  adapter: AuthAdapter;
}) {
  const [options, setOptions] = useState<RegistrationOptions | null>(null);
  const [siteCandidateId, setSiteCandidateId] = useState<string | null>(null);
  const [accountType, setAccountType] = useState<RegistrationAccountType | null>(null);
  const [rootUnitId, setRootUnitId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const optionRequests = useRef(new RegistrationSiteOptionsRequestGate());
  useEffect(() => {
    let alive = true;
    const request = optionRequests.current.begin(siteCandidateId);
    setError('');
    void adapter
      .loadRegistrationOptions(session.email, siteCandidateId)
      .then((value) => {
        if (!alive || !optionRequests.current.accepts(request, value)) return;
        setOptions(value);
        setSiteCandidateId((current) => value.selectedSiteCandidateId ?? current);
      })
      .catch(() => {
        if (alive) setError(GENERIC_ERROR);
      });
    return () => {
      alive = false;
    };
  }, [adapter, retry, session.email, siteCandidateId]);
  const siteSelectionReady =
    options?.complete === true &&
    siteCandidateId !== null &&
    options.selectedSiteCandidateId === siteCandidateId;
  const profiles = useMemo(
    () =>
      siteSelectionReady
        ? options?.profiles.filter((profile) => profile.accountType === accountType) ?? []
        : [],
    [accountType, options, siteSelectionReady],
  );
  const rootUnit = siteSelectionReady
    ? options?.organizationOptions.find((item) => item.rootUnitId === rootUnitId)
    : undefined;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const displayName = String(data.get('displayName') ?? '').trim();
    const requestedProfileId = String(data.get('requestedProfileId') ?? '');
    const contractorCandidateId = String(data.get('contractorCandidateId') ?? '');
    const childUnitId = String(data.get('childUnitId') ?? '');
    const phone = String(data.get('phone') ?? '').trim();
    const phonePurposeConsent = data.get('phonePurposeConsent') === 'on';
    const issue =
      displayName.length < 2
        ? '이름을 두 글자 이상 입력해 주세요.'
        : !siteSelectionReady
          ? '\uC0AC\uC5C5\uC7A5\uC744 \uC120\uD0DD\uD55C \uB4A4 \uC18C\uC18D \uC815\uBCF4\uB97C \uB2E4\uC2DC \uBD88\uB7EC\uC640 \uC8FC\uC138\uC694.'
          : !accountType
          ? '원청 또는 협력업체를 선택해 주세요.'
          : accountType === 'company' && !rootUnit
            ? '소속 부문을 선택해 주세요.'
            : accountType === 'company' &&
                rootUnit?.selectionMode === 'child-required' &&
                !rootUnit.children.some((item) => item.unitId === childUnitId)
              ? '소속 사업장을 선택해 주세요.'
              : accountType === 'contractor' &&
                  !options?.contractors.some((item) => item.candidateId === contractorCandidateId)
                ? '등록된 소속 협력업체를 선택해 주세요. 목록에 없으면 공장 안전담당자에게 업체 등록을 요청해 주세요.'
                : !profiles.some((item) => item.id === requestedProfileId)
                  ? '주로 하는 업무를 선택해 주세요.'
                  : phone && !normalizeContactPhone(phone)
                    ? '전화번호를 확인해 주세요.'
                    : phone && !phonePurposeConsent
                      ? '전화번호를 등록하려면 긴급 연락 목적의 이용에 동의해 주세요.'
                      : null;
    if (issue || !accountType) {
      setError(issue ?? '소속 유형을 선택해 주세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await adapter.completeRegistration({
        preRegistrationIntentId: session.preRegistrationIntentId,
        siteCandidateId,
        displayName,
        accountType,
        requestedProfileId: requestedProfileId as RegistrationProfileId,
        organizationCandidate:
          accountType === 'company' ? { rootUnitId, childUnitId: childUnitId || null } : null,
        contractorCandidateId: accountType === 'contractor' ? contractorCandidateId : null,
        phone: phone || null,
        phonePurposeConsent: Boolean(phone) && phonePurposeConsent,
      });
      sessionStorage.removeItem('kop-registration-name');
    } catch {
      setError('가입 신청을 보내지 못했습니다. 소속 정보를 확인한 뒤 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="access-shell registration-shell">
      <AccessBackground />
      <section className="access-panel">
        <div className="access-form-wrap registration-panel">
          <AccessIdentity />
          <header>
            <p className="eyebrow">이메일 인증 완료</p>
            <h2>가입 마무리</h2>
            <p>소속과 주로 하는 업무를 신청하세요. 최종 접근 범위는 관리자 승인 후 적용됩니다.</p>
          </header>
          {!options ? (
            <div className="registration-loading" role="status">
              {error ? (
                <button className="system-secondary" onClick={() => setRetry((value) => value + 1)}>
                  선택 항목 다시 불러오기
                </button>
              ) : (
                <>
                  <LoaderCircle className="spin" size={18} /> 선택 항목을 불러오는 중입니다.
                </>
              )}
            </div>
          ) : (
            <form className="system-form" onSubmit={submit} noValidate>
              <label>
                <span>이름</span>
                <div className="field-control">
                  <UserRound size={18} />
                  <input
                    name="displayName"
                    autoComplete="name"
                    defaultValue={sessionStorage.getItem('kop-registration-name') ?? ''}
                  />
                </div>
              </label>
              <label>
                <span>{'\uC0AC\uC5C5\uC7A5'}</span>
                <select
                  value={siteCandidateId ?? ''}
                  disabled={busy || options.availableSiteCandidates.length === 1}
                  onChange={(event) => {
                    const nextCandidateId = event.target.value || null;
                    setSiteCandidateId(nextCandidateId);
                    setRootUnitId('');
                  }}
                >
                  <option value="" disabled>
                    {'\uC0AC\uC5C5\uC7A5\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.'}
                  </option>
                  {options.availableSiteCandidates.map((candidate) => (
                    <option key={candidate.candidateId} value={candidate.candidateId}>
                      {candidate.label}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="registration-kind">
                <legend>소속 유형</legend>
                <div className="access-tabs">
                  <button
                    type="button"
                    aria-pressed={accountType === 'company'}
                    onClick={() => setAccountType('company')}
                  >
                    한국오션플랫폼
                  </button>
                  <button
                    type="button"
                    aria-pressed={accountType === 'contractor'}
                    onClick={() => setAccountType('contractor')}
                  >
                    협력사
                  </button>
                </div>
              </fieldset>
              {siteSelectionReady && accountType && (
                <>
                  {accountType === 'company' ? (
                    <>
                      <label>
                        <span>소속 부문</span>
                        <select
                          value={rootUnitId}
                          onChange={(event) => setRootUnitId(event.target.value)}
                        >
                          <option value="" disabled>
                            선택하세요
                          </option>
                          {options.organizationOptions.map((item) => (
                            <option key={item.rootUnitId} value={item.rootUnitId}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      {rootUnit?.selectionMode === 'child-required' && (
                        <label>
                          <span>사업장</span>
                          <select key={rootUnitId} name="childUnitId" defaultValue="">
                            <option value="" disabled>
                              선택하세요
                            </option>
                            {rootUnit.children.map((item) => (
                              <option key={item.unitId} value={item.unitId}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </>
                  ) : (
                    <label>
                      <span>협력사</span>
                      <ContractorCandidateSelect
                        key={siteCandidateId}
                        contractors={options.contractors}
                      />
                    </label>
                  )}
                  <label>
                    <span>주로 하는 업무</span>
                    <select
                      key={`${siteCandidateId}-${accountType}`}
                      name="requestedProfileId"
                      defaultValue={profiles.length === 1 ? profiles[0]!.id : ''}
                    >
                      <option value="" disabled>
                        선택하세요
                      </option>
                      {profiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.label}
                        </option>
                      ))}
                    </select>
                    <small>업무 선택은 검토 참고 정보이며 권한을 직접 부여하지 않습니다.</small>
                  </label>
                </>
              )}
              <label>
                <span>
                  비상 연락처 <small>(지금 입력하거나 승인 후 등록)</small>
                </span>
                <div className="field-control">
                  <input name="phone" type="tel" autoComplete="tel" placeholder="010-0000-0000" />
                </div>
              </label>
              <label className="registration-consent">
                <input name="phonePurposeConsent" type="checkbox" />
                <span>업무상 긴급 연락 목적으로 전화번호를 사용하는 데 동의합니다.</span>
              </label>
              {error && (
                <p className="form-message error" role="alert">
                  {error}
                </p>
              )}
              <button
                className="system-primary"
                disabled={busy || !accountType || !siteSelectionReady}
                type="submit"
              >
                {busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />}
                <span>{busy ? '신청 중' : '승인 요청 보내기'}</span>
              </button>
            </form>
          )}
          <button className="system-secondary" onClick={() => adapter.signOut()}>
            다른 계정으로 로그인
          </button>
        </div>
      </section>
    </main>
  );
}

export function PendingScreen({
  session,
  adapter,
}: {
  session: PendingSession;
  adapter: AuthAdapter;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const checking = useRef(false);
  const lastCheck = useRef(0);
  const refresh = useCallback(async () => {
    if (checking.current) return;
    checking.current = true;
    lastCheck.current = Date.now();
    setBusy(true);
    setError('');
    try {
      await adapter.refreshApproval();
    } catch {
      setError('승인 상태를 확인하지 못했습니다. 다시 확인해 주세요.');
    } finally {
      checking.current = false;
      setBusy(false);
    }
  }, [adapter]);
  useEffect(() => {
    const checkOnReturn = () => {
      if (!document.hidden && Date.now() - lastCheck.current > 10_000) void refresh();
    };
    window.addEventListener('focus', checkOnReturn);
    document.addEventListener('visibilitychange', checkOnReturn);
    return () => {
      window.removeEventListener('focus', checkOnReturn);
      document.removeEventListener('visibilitychange', checkOnReturn);
    };
  }, [refresh]);
  return (
    <main className="access-shell pending-shell">
      <AccessBackground />
      <section className="access-panel">
        <div className="access-form-wrap pending-panel">
          <AccessIdentity />
          <CheckCircle2 size={30} />
          <header>
            <p className="eyebrow">요청이 접수되었습니다</p>
            <h2>승인 대기</h2>
            <p>관리자가 소속과 역할을 확인하고 있습니다.</p>
          </header>
          <p className="access-pending-boundary">
            승인 전에는 사업장·문서·사진을 포함한 업무 데이터에 접근할 수 없습니다.
          </p>
          <dl>
            <div>
              <dt>신청 계정</dt>
              <dd>{session.email}</dd>
            </div>
            <div>
              <dt>요청 범위</dt>
              <dd>{session.requestedScope}</dd>
            </div>
            <div>
              <dt>현재 상태</dt>
              <dd>승인 대기 · 업무 권한 없음</dd>
            </div>
            {session.accessRequest && (
              <div>
                <dt>신청 정보</dt>
                <dd>
                  {[
                    session.accessRequest.accountType === 'company'
                      ? session.accessRequest.organizationUnitDisplayName
                      : session.accessRequest.contractorDisplayName,
                    session.accessRequest.profileLabel,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </dd>
              </div>
            )}
          </dl>
          <button className="system-primary" onClick={refresh} disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />}
            <span>{busy ? '상태 확인 중' : '승인 상태 다시 확인'}</span>
          </button>
          {error && (
            <p className="form-message error" role="alert">
              {error}
            </p>
          )}
          <button className="system-secondary" onClick={() => adapter.signOut()}>
            다른 계정으로 로그인
          </button>
        </div>
      </section>
    </main>
  );
}
